import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  SwapHoriz as StatusIcon,
  VpnKey as LicenceIcon,
  Extension as PluginIcon,
  CheckCircle as LicensedIcon,
  Warning as ExpiringIcon,
  Block as UnlicensedIcon,
  ContentCopy as ContentCopyIcon,
  Language as DomainIcon,
} from '@mui/icons-material';

import PageHeader from '../../../components/common/PageHeader';
import SearchHeader from '../../../components/common/SearchHeader';
import MetricsCard from '../../../components/common/MetricsCard';
import ResponsiveTable from '../../../components/common/ResponsiveTable';
import PaginationComponent from '../../../components/common/PaginationComponent';
import ItemCard from '../../../components/common/ItemCard';
import LicenceService from '../services/licenceService';
import LicenceFormDialog from '../components/LicenceFormDialog';
import PluginManagerDialog from '../components/PluginManagerDialog';
import {
  LICENCE_STATUSES,
  formatDate,
  formatDateTime,
  formatPlanRange,
  statusColor,
  statusLabel,
} from '../components/licenceLabels';

const emptyStatistics = {
  total: 0,
  licensed: 0,
  expiring_soon: 0,
  unlicensed: 0,
  blocked: 0,
};

const LicenceListingPage = () => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));

  const [licences, setLicences] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statistics, setStatistics] = useState(emptyStatistics);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [productFilter, setProductFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalLicences, setTotalLicences] = useState(0);
  const [sortField, setSortField] = useState('issued_at');
  const [sortDirection, setSortDirection] = useState('desc');

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [selectedLicence, setSelectedLicence] = useState(null);
  const [createdKey, setCreatedKey] = useState(null);
  const [statusMenu, setStatusMenu] = useState(null);
  const [pluginsOpen, setPluginsOpen] = useState(false);

  const filterParams = useCallback(() => {
    const params = {};
    if (searchTerm.trim()) params.search = searchTerm.trim();
    if (statusFilter !== 'all') params.status = statusFilter;
    if (productFilter !== 'all') params.product_id = productFilter;
    return params;
  }, [searchTerm, statusFilter, productFilter]);

  const loadLicences = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await LicenceService.getAll({
        ...filterParams(),
        skip: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage,
        sort_by: sortField,
        sort_order: sortDirection,
      });
      if (result.success) {
        setLicences(result.data);
        setTotalLicences(result.total);
      } else {
        setError(result.error);
        setLicences([]);
        setTotalLicences(0);
      }
    } catch (loadError) {
      setError('Σφάλμα φόρτωσης αδειών');
      setLicences([]);
      setTotalLicences(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, sortField, sortDirection, filterParams]);

  const loadStatistics = useCallback(async () => {
    const result = await LicenceService.getStatistics(filterParams());
    setStatistics(result.success ? result.data : emptyStatistics);
  }, [filterParams]);

  const loadProducts = useCallback(async () => {
    const result = await LicenceService.getProducts();
    if (result.success) setProducts(result.data);
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    loadLicences();
    loadStatistics();
  }, [loadLicences, loadStatistics]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, productFilter]);

  const handleSort = (field, direction) => {
    setSortField(field);
    setSortDirection(direction);
    setCurrentPage(1);
  };

  const handleCreate = () => {
    setFormMode('create');
    setSelectedLicence(null);
    setFormOpen(true);
  };

  const handleEdit = async (licence) => {
    const result = await LicenceService.getById(licence.id);
    if (!result.success) {
      setError(result.error);
      return;
    }
    setFormMode('edit');
    setSelectedLicence(result.data);
    setFormOpen(true);
  };

  const handleSubmit = async (payload) => {
    const result = formMode === 'edit'
      ? await LicenceService.update(selectedLicence.id, payload)
      : await LicenceService.create(payload);
    if (result.success) {
      setFormOpen(false);
      if (formMode === 'create') {
        setCreatedKey(result.data.license_key);
      }
      loadLicences();
      loadStatistics();
    }
    return result;
  };

  const handleStatusChange = async (status) => {
    const licence = statusMenu?.licence;
    setStatusMenu(null);
    if (!licence || licence.status === status) return;
    const result = await LicenceService.updateStatus(licence.id, status);
    if (!result.success) {
      setError(result.error);
      return;
    }
    loadLicences();
    loadStatistics();
  };

  const handleUnblock = async (licenceId, domain) => {
    const confirmed = window.confirm(`Να επιτραπεί το ${domain} σε αυτό το κλειδί; Αν οι θέσεις δεν φτάνουν, το όριο μεγαλώνει.`);
    if (!confirmed) return;
    const result = await LicenceService.unblockDomain(licenceId, domain);
    if (!result.success) {
      setError(result.error);
      return;
    }
    loadLicences();
    loadStatistics();
  };

  const handleCopyCreatedKey = async () => {
    if (!createdKey) return;
    try {
      await navigator.clipboard.writeText(createdKey);
    } catch (copyError) {
      setError('Δεν ήταν δυνατή η αντιγραφή του κλειδιού');
    }
  };

  const columns = [
    {
      field: 'license_key_masked',
      headerName: 'Κλειδί',
      sortable: false,
      minWidth: 140,
      renderCell: (value) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
          {value}
        </Typography>
      ),
    },
    {
      field: 'plugin_name',
      headerName: 'Plugin',
      sortable: true,
      sortField: 'plugin',
      minWidth: 180,
    },
    {
      field: 'plan_display_name',
      headerName: 'Πλάνο',
      sortable: true,
      sortField: 'plan',
      minWidth: 170,
      renderCell: (value, row) => formatPlanRange(row.plan_duration_months),
    },
    {
      field: 'customer_name',
      headerName: 'Πελάτης',
      sortable: true,
      sortField: 'customer',
      minWidth: 180,
      renderCell: (value, row) => (
        <Box>
          <Typography variant="body2">{value || '—'}</Typography>
          {row.customer_email && (
            <Typography variant="caption" color="text.secondary">
              {row.customer_email}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Κατάσταση',
      sortable: true,
      minWidth: 160,
      renderCell: (value, row) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          <Chip size="small" label={statusLabel(value)} color={statusColor(value)} />
          {!row.is_licensed && (
            <Chip size="small" label="Unlicensed" variant="outlined" color="error" />
          )}
        </Box>
      ),
    },
    {
      field: 'expires_at',
      headerName: 'Λήξη',
      sortable: true,
      minWidth: 120,
      renderCell: (value) => formatDate(value) === '—' ? 'Χωρίς λήξη' : formatDate(value),
    },
    {
      field: 'activation_count',
      headerName: 'Θέσεις',
      sortable: true,
      sortField: 'seats',
      minWidth: 100,
      renderCell: (value, row) => {
        const used = value || 0;
        const limit = row.max_activations || 0;
        return (
          <Typography
            variant="body2"
            color={used > limit ? 'error.main' : 'text.primary'}
          >
            {used} / {limit}
          </Typography>
        );
      },
    },
    {
      field: 'domains',
      headerName: 'Domains',
      sortable: false,
      minWidth: 180,
      renderCell: (value, row) => {
        const domains = value || [];
        const blocked = row.blocked_domains || [];
        if (!domains.length && !blocked.length) return '—';
        return (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {domains.map((domain) => (
              <Chip key={domain} size="small" label={domain} variant="outlined" />
            ))}
            {blocked.map((domain) => (
              <Chip
                key={`blocked-${domain}`}
                size="small"
                color="error"
                label={`${domain} · block`}
                onDelete={() => handleUnblock(row.id, domain)}
                deleteIcon={<Typography variant="caption" sx={{ color: 'inherit', pr: 0.5 }}>unblock</Typography>}
              />
            ))}
          </Box>
        );
      },
    },
    {
      field: 'plugin_version',
      headerName: 'Έκδοση',
      sortable: true,
      minWidth: 100,
      renderCell: (value) => value || '—',
    },
    {
      field: 'last_seen',
      headerName: 'Εμφάνιση',
      sortable: true,
      minWidth: 150,
      renderCell: (value) => formatDateTime(value),
    },
    {
      field: 'actions',
      headerName: 'Ενέργειες',
      sortable: false,
      minWidth: 110,
      renderCell: (value, row) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton size="small" title="Επεξεργασία" onClick={() => handleEdit(row)}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            title="Αλλαγή κατάστασης"
            onClick={(event) => setStatusMenu({ anchor: event.currentTarget, licence: row })}
          >
            <StatusIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  const renderLicenceCard = (licence) => (
    <ItemCard
      key={licence.id}
      title={licence.license_key_masked}
      subtitle={licence.plugin_name}
      headerActions={[
        <Chip
          key="status"
          size="small"
          label={statusLabel(licence.status)}
          color={statusColor(licence.status)}
        />,
        !licence.is_licensed ? (
          <Chip key="unlicensed" size="small" label="Unlicensed" variant="outlined" color="error" />
        ) : null,
      ].filter(Boolean)}
      basicInfo={[
        { icon: <LicenceIcon fontSize="small" color="action" />, text: formatPlanRange(licence.plan_duration_months) },
        { icon: <DomainIcon fontSize="small" color="action" />, text: `${licence.activation_count || 0} / ${licence.max_activations || 0} θέσεις` },
      ]}
      expandedContent={(
        <Box>
          <Typography variant="body2">{licence.customer_name || '—'}</Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Λήξη: {licence.expires_at ? formatDate(licence.expires_at) : 'Χωρίς λήξη'}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Domains: {(licence.domains || []).join(', ') || '—'}
            {(licence.blocked_domains || []).length ? ` · Block: ${licence.blocked_domains.join(', ')}` : ''}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Έκδοση: {licence.plugin_version || '—'} · {formatDateTime(licence.last_seen)}
          </Typography>
        </Box>
      )}
      actions={[
        <IconButton key="edit" size="small" onClick={() => handleEdit(licence)}>
          <EditIcon fontSize="small" />
        </IconButton>,
        <IconButton
          key="status"
          size="small"
          onClick={(event) => setStatusMenu({ anchor: event.currentTarget, licence })}
        >
          <StatusIcon fontSize="small" />
        </IconButton>,
      ]}
    />
  );

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Άδειες"
        subtitle="Διαχείριση κλειδιών άδειας"
        icon="🔑"
        actions={[
          <IconButton
            key="plugins"
            title="Plugins"
            onClick={() => setPluginsOpen(true)}
            sx={{ border: '1px solid', borderColor: 'primary.main', color: 'primary.main' }}
          >
            <PluginIcon />
          </IconButton>,
          <IconButton
            key="create"
            title="Νέο κλειδί"
            onClick={handleCreate}
            sx={{ backgroundColor: 'primary.main', color: 'white', '&:hover': { backgroundColor: 'primary.dark' } }}
          >
            <AddIcon />
          </IconButton>,
        ]}
      />

      <Grid container spacing={isXs ? 1 : 2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <MetricsCard title="Σύνολο κλειδιών" value={statistics.total} icon={<LicenceIcon />} color="primary" />
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricsCard title="Licensed" value={statistics.licensed} icon={<LicensedIcon />} color="success" />
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricsCard title="Λήγουν σε 30 ημέρες" value={statistics.expiring_soon} icon={<ExpiringIcon />} color="warning" />
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricsCard title="Unlicensed" value={statistics.unlicensed} icon={<UnlicensedIcon />} color="error" />
        </Grid>
      </Grid>

      {statistics.blocked > 0 && (
        <Typography color="error" variant="body2" sx={{ mb: 2 }}>
          {statistics.blocked} {statistics.blocked === 1 ? 'κλειδί έχει' : 'κλειδιά έχουν'} domain σε block. Πάτα unblock στη γραμμή για να το επιτρέψεις.
        </Typography>
      )}

      <SearchHeader
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Αναζήτηση κλειδιού, πελάτη ή domain..."
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        statusOptions={[
          { value: 'all', label: 'Όλες' },
          ...LICENCE_STATUSES,
        ]}
        sortField={sortField}
        sortDirection={sortDirection}
        onSortChange={handleSort}
        sortOptions={[
          { value: 'issued_at', label: 'Ημερομηνία' },
          { value: 'plugin', label: 'Plugin' },
          { value: 'customer', label: 'Πελάτης' },
          { value: 'expires_at', label: 'Λήξη' },
          { value: 'last_seen', label: 'Εμφάνιση' },
        ]}
        extraFilters={(
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Plugin</InputLabel>
            <Select
              value={productFilter}
              label="Plugin"
              onChange={(event) => setProductFilter(event.target.value)}
            >
              <MenuItem value="all">Όλα</MenuItem>
              {products.map((product) => (
                <MenuItem key={product.id} value={product.id}>{product.plugin_name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      />

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
      )}

      <ResponsiveTable
        data={licences}
        columns={columns}
        loading={loading}
        emptyMessage="Δεν βρέθηκαν κλειδιά"
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
        renderMobileCard={renderLicenceCard}
      />

      <PaginationComponent
        currentPage={currentPage}
        totalPages={Math.ceil(totalLicences / itemsPerPage) || 1}
        totalItems={totalLicences}
        itemsPerPage={itemsPerPage}
        onPageChange={setCurrentPage}
        onItemsPerPageChange={(value) => {
          setItemsPerPage(value);
          setCurrentPage(1);
        }}
      />

      <PluginManagerDialog
        open={pluginsOpen}
        onClose={() => setPluginsOpen(false)}
        onSaved={loadProducts}
      />

      <LicenceFormDialog
        open={formOpen}
        mode={formMode}
        licence={selectedLicence}
        products={products}
        onClose={() => setFormOpen(false)}
        onSubmit={handleSubmit}
      />

      <Menu
        anchorEl={statusMenu?.anchor}
        open={Boolean(statusMenu)}
        onClose={() => setStatusMenu(null)}
      >
        {LICENCE_STATUSES.map((option) => (
          <MenuItem
            key={option.value}
            selected={statusMenu?.licence?.status === option.value}
            onClick={() => handleStatusChange(option.value)}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>

      <Dialog open={Boolean(createdKey)} onClose={() => setCreatedKey(null)} fullWidth maxWidth="sm">
        <DialogTitle>Το κλειδί δημιουργήθηκε</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Αντίγραψέ το τώρα. Στο listing φαίνεται κομμένο στη μέση.
          </Typography>
          <TextField
            fullWidth
            value={createdKey || ''}
            InputProps={{
              readOnly: true,
              sx: { fontFamily: 'monospace' },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleCopyCreatedKey} edge="end" title="Αντιγραφή">
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setCreatedKey(null)}>Κλείσιμο</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LicenceListingPage;
